import React from "react";
import CrisisNotice from "../components/CrisisNotice";
import GroundingCarousel from "../components/GroundingCarousel";
import config from "../config";
import useContent, { useSiteSettings } from "../hooks/useContent";
import DownloadList, { downloadsFor } from "../components/DownloadList";

/*
 * Resources: the owner-editable `resources` directory, `grounding` carousel,
 * `downloads` and settings `notes` (bundled copy until the API answers).
 * The crisis card and the page's own wording stay in code (contract §3.13.1).
 */

const CONTACT_EMAIL = config.business.contactEmail;

const telHref = (number) => `tel:${String(number).replace(/[^\d+]/g, "")}`;

// "@handle" -> Instagram profile link; bare domain/path -> https link.
function EntryLinks({ entry }) {
  const parts = [];
  (entry.phones || []).forEach((n) => parts.push(<a key={n} href={telHref(n)}>{n}</a>));
  if (entry.website) {
    parts.push(
      <a key="web" href={`https://${entry.website}`} target="_blank" rel="noreferrer">{entry.website}</a>
    );
  }
  if (entry.instagram) {
    parts.push(
      <a key="ig" href={`https://www.instagram.com/${entry.instagram.replace(/[^A-Za-z0-9._]/g, "")}`} target="_blank" rel="noreferrer">
        {entry.instagram}
      </a>
    );
  }
  if (entry.facebook) parts.push(<span key="fb">Facebook: {entry.facebook}</span>);
  if (!parts.length) return null;
  return parts.map((p, i) => <React.Fragment key={i}>{i > 0 && " · "}{p}</React.Fragment>);
}

/** Printable PDFs placed on this page (`downloads`, placement "resources"). */
function Downloads({ items }) {
  const files = downloadsFor(items, "resources");
  if (!files.length) return null;
  return (
    <div className="card policy-content">
      <h2>Downloads</h2>
      <DownloadList items={files} />
    </div>
  );
}

export default function Resources() {
  const { items: resourceGroups } = useContent("resources");
  const { items: grounding } = useContent("grounding");
  const { items: downloads } = useContent("downloads");
  const { notes } = useSiteSettings();
  const groundingImages = grounding
    .filter(({ data }) => data.image)
    .map(({ data }) => ({ media: data.image, caption: data.caption }));

  return (
    <section className="section white">
      <div className="container">
        <h1>Resources</h1>

        <CrisisNotice variant="card" />

        <p className="muted">
          Looking for Additional Mental Health Support? Below is a general list of local practitioners and resources that may be helpful.
        </p>

        <div className="resources-grid" style={{ marginTop: "1.25rem" }}>
          {/* Local Resources */}
          <div className="card policy-content">
            <h2>Local Resources</h2>
            <p className="muted">
              {notes.resources_disclaimer ? `${notes.resources_disclaimer} ` : ""}Know of a resource that should be added? Email{" "}
              <a href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a>.
            </p>

            {resourceGroups.map(({ slug, data: group }) => (
              <React.Fragment key={slug}>
                <h3>{group.title}</h3>
                <ul className="resource-list">
                  {(group.entries || []).map((entry, i) => (
                    <li key={i}>
                      <strong>{entry.name}</strong>
                      {entry.location && <> · {entry.location}</>}
                      <br />
                      <EntryLinks entry={entry} />
                      {entry.description && <p>{entry.description}</p>}
                    </li>
                  ))}
                </ul>
              </React.Fragment>
            ))}

            {notes.family_islands_note ? (
              <>
                <h3>A note on the Family Islands</h3>
                <p>{notes.family_islands_note}</p>
              </>
            ) : null}
            <p>
              Know of a resource that should be included here? Reach out at{" "}
              <a href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a> and help keep this list growing.
            </p>
          </div>

          {/* Grounding Techniques + Carousel */}
          <div className="card policy-content">
            <h2>Grounding Techniques</h2>
            <p>
              These grounding exercises are offered as gentle supports: an invitation to pause, breathe, and acknowledge what you are feeling. They can be used anytime you need a moment to come back to yourself.
            </p>
            <p>
              <em>If you notice increased anxiety or distress while using them, it is okay to stop. You know your body best.</em>
            </p>

            {/* Portrait-friendly carousel */}
            {groundingImages.length > 0 && (
              <div className="certs-wrap" style={{ marginTop: ".75rem" }}>
                <GroundingCarousel images={groundingImages} />
              </div>
            )}
          </div>

          <Downloads items={downloads} />
        </div>
      </div>
    </section>
  );
}
